// -----------------------------------------------------------------------------
//  Retrying the errors that are not really errors.
//
//  Observed in the field on the first successful sign-in, 19 August 2026:
//  the two startup queries fired in the same instant the session was created,
//  and one came back "JWT issued at future". The token's issued-at timestamp
//  was a fraction of a second ahead of the server's clock, so PostgREST
//  refused it. The other query, milliseconds later, succeeded.
//
//  Two reasons this matters more than it looks:
//
//    * Cheap Android handsets drift. This will not be rare in a village.
//    * "JWT issued at future" is not a sentence a field officer can act on.
//      An error message that cannot be acted on is worse than a short pause,
//      because it teaches people the app is unreliable.
//
//  So: retry once, quietly, on the small set of errors that are genuinely
//  transient. Everything else surfaces immediately and unchanged — a retry
//  loop that swallows real failures is how a broken app looks healthy.
// -----------------------------------------------------------------------------

const TRANSIENT = [
  'jwt issued at future',   // client/server clock skew at sign-in
  'jwt expired',            // token refreshed a moment later
  'failed to fetch',        // signal dropped mid-request
  'networkerror',
  'load failed',
]

function isTransient(message: string | undefined): boolean {
  if (!message) return false
  const m = message.toLowerCase()
  return TRANSIENT.some((t) => m.includes(t))
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Run a Supabase query, retrying once after a short pause if it fails with a
 * transient error.
 *
 * Takes a FUNCTION rather than a promise, because a promise has already
 * started: to retry we must be able to build the request again.
 */
export async function withRetry<T extends { error: { message: string } | null }>(
  run: () => PromiseLike<T>,
  delayMs = 1200,
): Promise<T> {
  const first = await run()
  if (!first.error || !isTransient(first.error.message)) return first

  await sleep(delayMs)
  const second = await run()

  if (second.error && isTransient(second.error.message)) {
    // Still failing. Say something a person standing in a village can use.
    return {
      ...second,
      error: {
        ...second.error,
        message:
          'Could not reach the server. Check your connection, and check that the date and time on this phone are correct.',
      },
    }
  }
  return second
}
